import { logManager } from '../logs'

import Peer from '../Peer'

// Libraries.
import { v4 as uuidV4 } from 'uuid'
import EventEmitter from 'eventemitter3'

/**
 * Manager for Peer objects.
 * Allows the creation and retrieval of peer objects.
 * @method PeerManager
 */
export default function PeerManager (managers) {
  const log = logManager.getLogger('Manager', 'Peer')

  // Internal variables.
  const trackManager = managers.trackManager
  const peers = new Map()
  const emitter = new EventEmitter()

  /**
   * Retrieve a snapshot of all Peer objects' current state.
   * @method getState
   * @return {Array}
   */
  function getState () {
    return getAll().map(id => get(id).getState())
  }

  /**
   * Create a new Peer using the provided configs.
   * @method create
   * @param  {Object} [config={}]
   * @return {Peer}
   */
  function create (config = {}) {
    const peer = new Peer(uuidV4(), config, trackManager)
    peer.once('peer:closed', id => peers.delete(id))
    peers.set(peer.id, peer)
    emitter.emit('peer:new', peer.id)
    return peer
  }

  /**
   * Retrieves an existing Peer object.
   * @method get
   * @param  {String} id ID of the desired Peer object.
   * @return {Peer}
   */
  function get (id) {
    const peer = peers.get(id)
    if (!peer) {
      log.debug(`No Peer found with ID: ${id}.`)
    }
    return peer
  }

  /**
   * Retrieve a list of all existing Peer object's IDs.
   * @method getAll
   * @return {Array} List of Peer IDs.
   */
  function getAll () {
    return Array.from(peers.keys())
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    getState,
    get,
    getAll,
    create,
    // Event APIs.
    on,
    once,
    off
  }
}
